import React from 'react';
import { heroData, introData } from '../data/mock';
import { Button } from './ui/button';
import { ArrowRight, Sparkles, Quote, CheckCircle, Award, Users, Building2 } from 'lucide-react';

const Hero = () => {
  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center pt-80 overflow-hidden bg-purple-100/60">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Floating Orbs */}
          <div className="absolute top-20 right-[10%] w-72 h-72 bg-[#7ed321]/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-20 left-[5%] w-96 h-96 bg-[#6b2d7b]/15 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
          <div className="absolute top-[40%] left-[20%] w-48 h-48 bg-[#7ed321]/10 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '2s' }} />
          
          {/* Subtle Grid Pattern */}
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%236b2d7b' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-16">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Content */}
            <div className="space-y-8">
              {/* Animated Headline */}
              <h1 className="text-5xl lg:text-6xl xl:text-7xl font-bold leading-tight">
                <span className="bg-gradient-to-r from-[#6b2d7b] via-[#8b3d9b] to-[#6b2d7b] bg-clip-text text-transparent bg-[length:200%_auto] animate-gradient">
                  Bringing Clarity,
                </span>
                <br />
                <span className="bg-gradient-to-r from-[#6b2d7b] via-[#8b3d9b] to-[#6b2d7b] bg-clip-text text-transparent bg-[length:200%_auto] animate-gradient" style={{ animationDelay: '0.5s' }}>
                  Structure &
                </span>
                <br />
                <span className="bg-gradient-to-r from-[#7ed321] via-[#9be850] to-[#7ed321] bg-clip-text text-transparent bg-[length:200%_auto] animate-gradient" style={{ animationDelay: '1s' }}>
                  Momentum
                </span>
              </h1>

              {/* Subheadline */}
              <p className="text-xl text-slate-600 leading-relaxed max-w-xl">
                {heroData.subheadline}
              </p>

              {/* CTAs with Enhanced Styling */}
              <div className="flex flex-wrap gap-4">
                <Button
                  onClick={() => scrollToSection('#contact')}
                  className="bg-gradient-to-r from-[#7ed321] to-[#6bc11a] hover:from-[#6bc11a] hover:to-[#5aa815] text-[#1a1a1a] font-semibold px-8 py-6 text-lg group shadow-lg shadow-[#7ed321]/30 hover:shadow-xl hover:shadow-[#7ed321]/40 transition-all duration-300"
                >
                  {heroData.ctaPrimary}
                  <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
                </Button>
                <Button
                  variant="outline"
                  onClick={() => scrollToSection('#services')}
                  className="border-2 border-[#6b2d7b] text-[#6b2d7b] hover:bg-[#6b2d7b] hover:text-white px-8 py-6 text-lg transition-all duration-300"
                >
                  {heroData.ctaSecondary}
                </Button>
              </div>

              {/* Trust Indicators */}
              <div className="flex flex-wrap items-center gap-6 pt-4">
                <div className="flex items-center gap-2 text-slate-600">
                  <div className="w-10 h-10 rounded-full bg-[#7ed321]/20 flex items-center justify-center">
                    <Award className="w-5 h-5 text-[#6b2d7b]" />
                  </div>
                  <span className="text-sm font-medium">CPM® Certified</span>
                </div>
                <div className="flex items-center gap-2 text-slate-600">
                  <div className="w-10 h-10 rounded-full bg-[#7ed321]/20 flex items-center justify-center">
                    <Users className="w-5 h-5 text-[#6b2d7b]" />
                  </div>
                  <span className="text-sm font-medium">1000+ Trained</span>
                </div>
                <div className="flex items-center gap-2 text-slate-600">
                  <div className="w-10 h-10 rounded-full bg-[#7ed321]/20 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-[#6b2d7b]" />
                  </div>
                  <span className="text-sm font-medium">40+ Years</span>
                </div>
              </div>
            </div>

            {/* Hero Image Collage */}
            <div className="relative">
              {/* Glowing Background */}
              <div className="absolute -inset-4 bg-gradient-to-r from-[#7ed321]/30 via-[#6b2d7b]/20 to-[#7ed321]/30 rounded-[2rem] blur-2xl opacity-60 animate-pulse" />
              
              {/* Collage Grid */}
              <div className="relative z-10 grid grid-cols-2 gap-3">
                {/* Large Image - Top Left */}
                <div className="row-span-2 rounded-2xl overflow-hidden shadow-xl border-2 border-white/50 transform hover:scale-[1.02] transition-transform duration-300">
                  <img
                    src="https://customer-assets.emergentagent.com/job_clark-redesign/artifacts/7xuu9s2z_img2427.jpg"
                    alt="Industry networking reception"
                    className="w-full h-full object-cover"
                  />
                </div>
                
                {/* Top Right Image */}
                <div className="rounded-2xl overflow-hidden shadow-xl border-2 border-white/50 transform hover:scale-[1.02] transition-transform duration-300">
                  <img
                    src="https://customer-assets.emergentagent.com/job_clark-redesign/artifacts/641lrer1_2885.jpg"
                    alt="Group photo at venue"
                    className="w-full h-full object-cover aspect-[4/3]"
                  />
                </div>
                
                {/* Bottom Right Image */}
                <div className="rounded-2xl overflow-hidden shadow-xl border-2 border-white/50 transform hover:scale-[1.02] transition-transform duration-300">
                  <img
                    src="https://customer-assets.emergentagent.com/job_clark-redesign/artifacts/urt6qwz4_3987.jpg"
                    alt="Team event celebration"
                    className="w-full h-full object-cover aspect-[4/3]"
                  />
                </div>
              </div>
              
              {/* Floating Stats Card */}
              <div className="absolute -bottom-6 -left-6 bg-white/90 backdrop-blur-md rounded-2xl p-5 shadow-xl border border-white/50 z-20 transform hover:scale-105 transition-transform duration-300">
                <div className="text-4xl font-bold bg-gradient-to-r from-[#7ed321] to-[#6bc11a] bg-clip-text text-transparent">40+</div>
                <div className="text-sm text-slate-600 font-medium">Years of Experience</div>
              </div>
              
              {/* Floating Badge - Top Right */}
              <div className="absolute -top-4 -right-4 bg-[#6b2d7b] text-white rounded-full px-4 py-2 shadow-lg z-20 flex items-center gap-2 transform hover:scale-105 transition-transform duration-300">
                <CheckCircle className="w-4 h-4 text-[#7ed321]" />
                <span className="text-sm font-semibold">Trusted Expert</span>
              </div>
              
              {/* Decorative Elements */}
              <div className="absolute -top-8 -right-8 w-24 h-24 bg-gradient-to-br from-[#7ed321] to-[#6bc11a] rounded-2xl -z-10 rotate-12 opacity-80" />
              <div className="absolute -bottom-4 right-12 w-16 h-16 bg-gradient-to-br from-[#6b2d7b] to-[#8b3d9b] rounded-xl -z-10 -rotate-6 opacity-70" />
            </div>
          </div>
        </div>
      </section>

      {/* Intro Section with Enhanced Design */}
      <section className="py-20 bg-purple-100 relative overflow-hidden">
        {/* Background Decoration */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-[#7ed321]/5 to-[#6b2d7b]/5 rounded-full blur-3xl" />
        
        <div className="relative max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center space-y-8">
            <h2 className="text-3xl lg:text-4xl font-bold text-[#6b2d7b]">
              {introData.title}
            </h2>
            
            <p className="text-lg text-slate-600 leading-relaxed max-w-4xl mx-auto">
              {introData.description}
            </p>
            
            {/* Highlight Quote with Enhanced Styling */}
            <div className="relative bg-gradient-to-br from-white to-purple-50/50 rounded-3xl p-8 shadow-xl border border-purple-100 max-w-3xl mx-auto transform hover:scale-[1.02] transition-transform duration-300">
              {/* Quote Icon Background */}
              <div className="absolute -top-6 left-8 w-12 h-12 bg-gradient-to-br from-[#7ed321] to-[#6bc11a] rounded-full flex items-center justify-center shadow-lg">
                <Quote className="w-6 h-6 text-white" />
              </div>
              <p className="text-xl text-[#6b2d7b] font-medium italic leading-relaxed pt-4">
                "{introData.highlight}"
              </p>
              {/* Decorative Corner */}
              <div className="absolute bottom-0 right-0 w-20 h-20 bg-gradient-to-tl from-[#7ed321]/10 to-transparent rounded-br-3xl" />
            </div>
            
            <p className="text-lg text-slate-600 leading-relaxed max-w-4xl mx-auto">
              {introData.focus}
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default Hero;
